import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { useAuth } from "@/App";
import { 
  ChevronLeft,
  ArrowLeft,
  Play,
  Pause,
  RotateCcw,
  Volume2,
  VolumeX
} from "lucide-react";

const BREATHING_PATTERNS = [
  {
    id: "relaxing",
    name: "4-7-8 Relaxing",
    description: "Best for relaxation and sleep",
    inhale: 4,
    hold1: 7,
    exhale: 8,
    hold2: 0,
    color: "from-[#d0dce8] to-[#c8d0e0]"
  },
  {
    id: "box",
    name: "Box Breathing",
    description: "Great for focus and calm",
    inhale: 4,
    hold1: 4,
    exhale: 4,
    hold2: 4,
    color: "from-[#d8d0e8] to-[#d0c8e0]"
  },
  {
    id: "simple",
    name: "Simple Breathing",
    description: "Easy and calming",
    inhale: 4,
    hold1: 0,
    exhale: 6,
    hold2: 0,
    color: "from-[#d4e8d8] to-[#c8e0d0]"
  }
];

export default function BreathingExercisePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle, inhale, hold1, exhale, hold2
  const [timeLeft, setTimeLeft] = useState(0);
  const [cycles, setCycles] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const intervalRef = useRef(null);
  const audioContextRef = useRef(null);

  const playTone = (frequency = 440, duration = 0.2) => {
    if (!soundEnabled) return;
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = "sine";
      
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (e) {
      // Audio API not supported in this browser/context - silent fallback
      if (process.env.NODE_ENV === 'development') {
        console.warn('Audio playback not supported:', e.message);
      }
    }
  };

  useEffect(() => {
    if (!isPlaying || !selectedPattern) return;

    const pattern = selectedPattern;
    const phases = [
      { name: "inhale", duration: pattern.inhale, label: "Breathe In" },
      { name: "hold1", duration: pattern.hold1, label: "Hold" },
      { name: "exhale", duration: pattern.exhale, label: "Breathe Out" },
      { name: "hold2", duration: pattern.hold2, label: "Hold" }
    ].filter(p => p.duration > 0);

    let currentPhaseIndex = 0;
    let currentTime = phases[0].duration;

    setPhase(phases[0].name);
    setTimeLeft(phases[0].duration);
    playTone(phases[0].name === "inhale" ? 523 : 392);

    intervalRef.current = setInterval(() => {
      currentTime -= 1;
      
      if (currentTime <= 0) {
        currentPhaseIndex = (currentPhaseIndex + 1) % phases.length;
        
        if (currentPhaseIndex === 0) {
          setCycles(prev => prev + 1);
        }
        
        const nextPhase = phases[currentPhaseIndex];
        currentTime = nextPhase.duration;
        setPhase(nextPhase.name);
        playTone(nextPhase.name === "inhale" ? 523 : nextPhase.name === "exhale" ? 392 : 440);
      }
      
      setTimeLeft(currentTime);
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, selectedPattern, soundEnabled]);

  const handleStart = () => {
    setIsPlaying(true);
    setCycles(0);
  };

  const handlePause = () => {
    setIsPlaying(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleReset = () => {
    setIsPlaying(false);
    setPhase("idle");
    setTimeLeft(0);
    setCycles(0);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const getPhaseLabel = () => {
    switch (phase) {
      case "inhale": return "Breathe In";
      case "hold1": return "Hold";
      case "exhale": return "Breathe Out";
      case "hold2": return "Hold";
      default: return "Ready";
    }
  };

  const getCircleScale = () => {
    if (!isPlaying) return 1;
    if (phase === "inhale") return 1.3;
    if (phase === "exhale") return 0.8;
    return 1;
  };

  // Pattern Selection Screen
  if (!selectedPattern) {
    return (
      <div className="min-h-screen bg-background pb-28">
        <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/50">
          <div className="px-4 pt-6 pb-4">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate("/compass")}
                className="p-2 -ml-2"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div>
                <h1 className="text-2xl font-heading text-foreground">Breathing Exercise</h1>
                <p className="text-foreground/50 text-sm">Choose a pattern</p>
              </div>
            </div>
          </div>
        </div>

        <main className="px-4 py-6 max-w-lg mx-auto">
          <p className="text-foreground/70 mb-6 text-center">
            Select a breathing pattern that suits your needs. Each pattern helps reduce stress and improve focus.
          </p>

          <div className="space-y-4">
            {BREATHING_PATTERNS.map((pattern) => (
              <Card 
                key={pattern.id}
                className="overflow-hidden cursor-pointer hover:shadow-md transition-all"
                onClick={() => setSelectedPattern(pattern)}
                data-testid={`pattern-${pattern.id}`}
              >
                <CardContent className="p-0">
                  <div className={`h-24 bg-gradient-to-br ${pattern.color} flex items-center justify-center`}>
                    <div className="text-center">
                      <p className="text-foreground font-heading text-lg">{pattern.name}</p>
                      <p className="text-foreground/60 text-sm">{pattern.description}</p>
                    </div>
                  </div>
                  <div className="p-4 bg-[#f5f9f5]">
                    <div className="flex justify-center gap-2 text-sm text-foreground/60">
                      <span>Inhale {pattern.inhale}s</span>
                      {pattern.hold1 > 0 && <span>• Hold {pattern.hold1}s</span>}
                      <span>• Exhale {pattern.exhale}s</span>
                      {pattern.hold2 > 0 && <span>• Hold {pattern.hold2}s</span>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </main>

        {user && <BottomNavigation />}
      </div>
    );
  }

  // Breathing Exercise Screen
  return (
    <div className={`min-h-screen bg-gradient-to-br ${selectedPattern.color} flex flex-col`}>
      <div className="p-4 flex items-center justify-between">
        <button 
          onClick={() => {
            handleReset();
            setSelectedPattern(null);
          }}
          className="w-10 h-10 rounded-full bg-white/40 flex items-center justify-center"
        >
          <ChevronLeft className="w-5 h-5 text-foreground" />
        </button>
        <span className="text-foreground/70 font-medium">{selectedPattern.name}</span>
        <button 
          onClick={() => setSoundEnabled(!soundEnabled)}
          className="w-10 h-10 rounded-full bg-white/40 flex items-center justify-center"
        >
          {soundEnabled ? (
            <Volume2 className="w-5 h-5 text-foreground" />
          ) : (
            <VolumeX className="w-5 h-5 text-foreground" />
          )}
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 pb-20">
        {/* Breathing Circle */}
        <div className="relative mb-8">
          {/* Outer ring */}
          <div className="w-64 h-64 rounded-full border-4 border-white/30 flex items-center justify-center">
            {/* Inner circle that animates */}
            <div 
              className="w-48 h-48 rounded-full bg-white/80 flex items-center justify-center shadow-lg transition-transform duration-1000 ease-in-out"
              style={{ transform: `scale(${getCircleScale()})` }}
            >
              <div className="text-center">
                <p className="text-foreground font-heading text-2xl mb-1">
                  {getPhaseLabel()}
                </p>
                {isPlaying && (
                  <p className="text-foreground/60 text-4xl font-heading">{timeLeft}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Progress indicator dot */}
          {isPlaying && (
            <div 
              className="absolute w-4 h-4 bg-white rounded-full shadow-md"
              style={{
                top: "50%",
                left: "50%",
                transform: `translate(-50%, -50%) rotate(${(1 - timeLeft / (selectedPattern[phase] || 1)) * 360}deg) translateY(-128px)`,
                transition: "transform 1s linear"
              }}
            />
          )}
        </div>

        {/* Cycles counter */}
        {cycles > 0 && (
          <p className="text-foreground/60 text-sm mb-8">
            {cycles} {cycles === 1 ? 'cycle' : 'cycles'} completed
          </p>
        )}

        {/* Controls */}
        <div className="flex items-center gap-4">
          {!isPlaying ? (
            <Button 
              className="btn-primary px-12 py-6 text-lg"
              onClick={handleStart}
              data-testid="start-breathing"
            >
              <Play className="w-6 h-6 mr-2" />
              Start
            </Button>
          ) : (
            <>
              <Button 
                variant="outline"
                className="w-14 h-14 rounded-full border-white/40 bg-white/20"
                onClick={handleReset}
              >
                <RotateCcw className="w-6 h-6 text-foreground" />
              </Button>
              <Button 
                className="btn-primary px-8 py-6"
                onClick={handlePause}
              >
                <Pause className="w-6 h-6 mr-2" />
                Pause
              </Button>
            </>
          )}
        </div>

        {/* Tips */}
        <Card className="glass-card mt-12 max-w-sm">
          <CardContent className="p-4 text-center">
            <p className="text-foreground/60 text-sm">
              {phase === "inhale" && "Breathe in slowly through your nose"}
              {phase === "hold1" && "Hold your breath gently"}
              {phase === "exhale" && "Exhale slowly through your mouth"}
              {phase === "hold2" && "Rest before the next breath"}
              {phase === "idle" && "Find a comfortable position and relax your shoulders"}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
